import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

import { ModeloVistoriaApiService } from '../services/modelo-vistoria-api.service';
import { ModeloVistoria } from '../models/modelo-vistoria';
import { MessageService } from '@services/message.service';
import { ComponentComAlteracoesNaoSalvas } from '../../../../shared/guards/alteracoes-nao-salvas.guard';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';

@Component({
  selector: 'app-editar-modelo-vistoria',
  standalone: false,
  templateUrl: './editar-modelo-vistoria.component.html',
  styleUrl: './editar-modelo-vistoria.component.scss'
})
export class EditarModeloVistoriaComponent implements OnInit, ComponentComAlteracoesNaoSalvas {

  modelo: ModeloVistoria;
  salvando = false;

  // Comparado com o estado atual do modelo pra saber se há algo não salvo — usado pela guarda de
  // rota que pergunta antes de sair da tela (ver AlteracoesNaoSalvasGuard).
  private snapshotSalvo = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private modeloVistoriaApiService: ModeloVistoriaApiService,
    private messageService: MessageService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.route.data.subscribe((data: Data) => {
      this.modelo = data['modelo'] as ModeloVistoria;
      this.snapshotSalvo = JSON.stringify(this.modelo);
    });
  }

  public possuiAlteracoesNaoSalvas(): boolean {
    return JSON.stringify(this.modelo) !== this.snapshotSalvo;
  }

  public salvarAsync(): Observable<boolean> {
    return this.executarSalvar();
  }

  get tituloPagina(): string {
    return this.modelo.id === 0 ? 'Novo modelo de vistoria' : 'Editar modelo de vistoria';
  }

  public salvar(): void {
    this.executarSalvar().subscribe(sucesso => { if (sucesso) this.voltar(); });
  }

  private executarSalvar(): Observable<boolean> {
    const erro = this.validar();
    if (erro) {
      this.messageService.showToastWarning(erro);
      return of(false);
    }

    this.salvando = true;
    const chamada$: Observable<number | void> = this.modelo.id === 0
      ? this.modeloVistoriaApiService.criar(this.modelo)
      : this.modeloVistoriaApiService.editar(this.modelo.id, this.modelo);

    return chamada$.pipe(
      tap((novoId) => {
        if (typeof novoId === 'number') this.modelo.id = novoId;
        this.messageService.showToastSuccess('Modelo salvo com sucesso');
        this.snapshotSalvo = JSON.stringify(this.modelo);
      }),
      map(() => true),
      catchError(() => of(false)),
      finalize(() => { this.salvando = false; }),
    );
  }

  public voltar(): void {
    this.router.navigate(['/vistoria/modelos']);
  }

  public templateClick(): void {
    if (this.modelo.id === 0 || this.possuiAlteracoesNaoSalvas()) {
      this.confirmarSalvarParaTemplate();
      return;
    }

    this.router.navigate(['/vistoria/modelos', this.modelo.id, 'template']);
  }

  private confirmarSalvarParaTemplate(): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Salvar antes de continuar';
    dialogRef.componentInstance.mensagem = 'É preciso salvar o modelo antes de editar o template do laudo. Deseja salvar agora?';
    dialogRef.componentInstance.textoBtnConfirmacao = 'Salvar';
    dialogRef.componentInstance.acaoConfirmacao = () => {
      this.executarSalvar().subscribe(sucesso => {
        if (sucesso) this.router.navigate(['/vistoria/modelos', this.modelo.id, 'template']);
      });
    };
  }

  private validar(): string | null {
    if (!this.modelo.nome?.trim()) return 'O nome do modelo não pode ser vazio';

    if (this.modelo.itens.some(c => !c.nome?.trim())) return 'Todos os itens de vistoria precisam ter um nome';
    if (this.modelo.itens.some(c => c.atributos.some(a => !a.nome?.trim())))
      return 'Todos os atributos precisam ter um nome';

    if (this.modelo.comodos.some(c => !c.nome?.trim())) return 'Todos os cômodos precisam ter um nome';

    return null;
  }
}
